import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";

import { ChatResponseMetricStatus } from "@fitness-app/data-models/entities/ChatResponseMetric";

import { supabase } from "~/store/initializeStore";

export interface TrainerMetricsAggregation {
  trainerId: string;
  totalResponses: number;
  totalBreached: number;
  totalPending: number;
  avgBusinessMinutes: number | null;
  avgActualMinutes: number | null;
  breachRate: number;
}

export interface HeatmapCell {
  dayOfWeek: number; // 0 = Monday, 6 = Sunday
  hour: number; // 0-23
  count: number;
}

export interface ResponseTimeDistribution {
  bucket: string;
  count: number;
  label: string;
}

export interface ChatResponseMetricsData {
  byTrainer: TrainerMetricsAggregation[];
  totals: {
    totalResponses: number;
    totalBreached: number;
    totalPending: number;
    avgBusinessMinutes: number | null;
    avgActualMinutes: number | null;
    breachRate: number;
  };
  heatmap: HeatmapCell[];
  responseTimeDistribution: ResponseTimeDistribution[];
}

export const useChatResponseMetrics = ({ month, trainerIds }: { month: Dayjs; trainerIds: string[] }) => {
  const startOfMonth = month.startOf("month").toISOString();
  const endOfMonth = month.endOf("month").toISOString();

  const query = useQuery(["chatResponseMetrics", month.format("YYYY-MM"), trainerIds], {
    queryFn: async (): Promise<ChatResponseMetricsData> => {
      if (trainerIds.length === 0) {
        return {
          byTrainer: [],
          totals: {
            totalResponses: 0,
            totalBreached: 0,
            totalPending: 0,
            avgBusinessMinutes: null,
            avgActualMinutes: null,
            breachRate: 0,
          },
          heatmap: [],
          responseTimeDistribution: [],
        };
      }

      let queryBuilder = supabase
        .from("chat_response_metric")
        .select("trainerId, status, responseDurationBusinessMinutes, responseDurationActualMinutes, createdAt")
        .gte("createdAt", startOfMonth)
        .lte("createdAt", endOfMonth);

      if (trainerIds.length === 1 && trainerIds[0] !== "all") {
        queryBuilder = queryBuilder.eq("trainerId", trainerIds[0]!);
      } else if (trainerIds.length > 1 && !trainerIds.includes("all")) {
        queryBuilder = queryBuilder.in("trainerId", trainerIds);
      }

      const { data } = await queryBuilder.throwOnError();

      // Aggregate by trainer
      const trainerMap = new Map<
        string,
        {
          totalResponses: number;
          totalBreached: number;
          totalPending: number;
          businessMinutesSum: number;
          businessMinutesCount: number;
          actualMinutesSum: number;
          actualMinutesCount: number;
        }
      >();

      data?.forEach((metric) => {
        const trainerId = metric.trainerId;
        if (!trainerMap.has(trainerId)) {
          trainerMap.set(trainerId, {
            totalResponses: 0,
            totalBreached: 0,
            totalPending: 0,
            businessMinutesSum: 0,
            businessMinutesCount: 0,
            actualMinutesSum: 0,
            actualMinutesCount: 0,
          });
        }

        const stats = trainerMap.get(trainerId)!;

        if (metric.status === ChatResponseMetricStatus.Responded) {
          stats.totalResponses++;
          if (metric.responseDurationBusinessMinutes !== null) {
            stats.businessMinutesSum += metric.responseDurationBusinessMinutes;
            stats.businessMinutesCount++;
          }
          if (metric.responseDurationActualMinutes !== null) {
            stats.actualMinutesSum += metric.responseDurationActualMinutes;
            stats.actualMinutesCount++;
          }
        } else if (metric.status === ChatResponseMetricStatus.Breached) {
          stats.totalBreached++;
        } else if (metric.status === ChatResponseMetricStatus.Pending) {
          stats.totalPending++;
        }
      });

      const byTrainer: TrainerMetricsAggregation[] = Array.from(trainerMap.entries()).map(([trainerId, stats]) => {
        const total = stats.totalResponses + stats.totalBreached;
        return {
          trainerId,
          totalResponses: stats.totalResponses,
          totalBreached: stats.totalBreached,
          totalPending: stats.totalPending,
          avgBusinessMinutes:
            stats.businessMinutesCount > 0 ? Math.round(stats.businessMinutesSum / stats.businessMinutesCount) : null,
          avgActualMinutes:
            stats.actualMinutesCount > 0 ? Math.round(stats.actualMinutesSum / stats.actualMinutesCount) : null,
          breachRate: total > 0 ? Math.round((stats.totalBreached / total) * 100) : 0,
        };
      });

      // Calculate totals from raw trainer data (not from rounded per-trainer averages)
      const allStats = {
        totalResponses: 0,
        totalBreached: 0,
        totalPending: 0,
        businessMinutesSum: 0,
        businessMinutesCount: 0,
        actualMinutesSum: 0,
        actualMinutesCount: 0,
      };

      trainerMap.forEach((stats) => {
        allStats.totalResponses += stats.totalResponses;
        allStats.totalBreached += stats.totalBreached;
        allStats.totalPending += stats.totalPending;
        allStats.businessMinutesSum += stats.businessMinutesSum;
        allStats.businessMinutesCount += stats.businessMinutesCount;
        allStats.actualMinutesSum += stats.actualMinutesSum;
        allStats.actualMinutesCount += stats.actualMinutesCount;
      });

      const totalAll = allStats.totalResponses + allStats.totalBreached;

      // Build heatmap data (conversations by day of week and hour)
      const heatmapMap = new Map<string, number>();
      data?.forEach((metric) => {
        const date = dayjs(metric.createdAt);
        const dayOfWeek = date.day() === 0 ? 6 : date.day() - 1; // Convert to Mon=0, Sun=6
        const hour = date.hour();
        const key = `${dayOfWeek}-${hour}`;
        heatmapMap.set(key, (heatmapMap.get(key) || 0) + 1);
      });

      const heatmap: HeatmapCell[] = Array.from(heatmapMap.entries()).map(([key, count]) => {
        const parts = key.split("-").map(Number);
        return { dayOfWeek: parts[0] ?? 0, hour: parts[1] ?? 0, count };
      });

      // Build response time distribution with granular 1h+ buckets
      const distributionBuckets = [
        { bucket: "0-30s", label: "0 - 30 sek", maxMinutes: 0.5 },
        { bucket: "30-60s", label: "30 - 60 sek", maxMinutes: 1 },
        { bucket: "1-5min", label: "1 - 5 min", maxMinutes: 5 },
        { bucket: "5-15min", label: "5 - 15 min", maxMinutes: 15 },
        { bucket: "15-60min", label: "15 - 60 min", maxMinutes: 60 },
        { bucket: "1-2h", label: "1h+", maxMinutes: 120 },
        { bucket: "2-3h", label: "2h+", maxMinutes: 180 },
        { bucket: "3-4h", label: "3h+", maxMinutes: 240 },
        { bucket: "4h+", label: "4h+", maxMinutes: Infinity },
      ];

      const distributionCounts: Record<string, number> = {};
      distributionBuckets.forEach((b) => {
        distributionCounts[b.bucket] = 0;
      });

      data?.forEach((metric) => {
        // Include both Responded and Breached items that have response time
        if (
          (metric.status === ChatResponseMetricStatus.Responded ||
            metric.status === ChatResponseMetricStatus.Breached) &&
          metric.responseDurationBusinessMinutes !== null
        ) {
          const minutes = metric.responseDurationBusinessMinutes;
          for (const bucket of distributionBuckets) {
            if (minutes < bucket.maxMinutes || bucket.maxMinutes === Infinity) {
              distributionCounts[bucket.bucket] = (distributionCounts[bucket.bucket] ?? 0) + 1;
              break;
            }
          }
        }
      });

      const responseTimeDistribution: ResponseTimeDistribution[] = distributionBuckets.map((b) => ({
        bucket: b.bucket,
        label: b.label,
        count: distributionCounts[b.bucket] ?? 0,
      }));

      return {
        byTrainer,
        totals: {
          totalResponses: allStats.totalResponses,
          totalBreached: allStats.totalBreached,
          totalPending: allStats.totalPending,
          avgBusinessMinutes:
            allStats.businessMinutesCount > 0
              ? Math.round(allStats.businessMinutesSum / allStats.businessMinutesCount)
              : null,
          avgActualMinutes:
            allStats.actualMinutesCount > 0
              ? Math.round(allStats.actualMinutesSum / allStats.actualMinutesCount)
              : null,
          breachRate: totalAll > 0 ? Math.round((allStats.totalBreached / totalAll) * 100) : 0,
        },
        heatmap,
        responseTimeDistribution,
      };
    },
  });

  return query;
};
